import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _window, _window2;
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { getBscGlobalState, isSSR } from '../../common/utils';
import { Logger } from '../../common/utils/logger';
import { Status } from '../../types';
import { BSCIndexedDB } from '../indexed-db-service';
import { WebStorageType } from './types';
import { getStorageItem, setStorageItem, setStrictlyNecessaryStorageItem } from './utils';
var AtlBrowserStorage = /*#__PURE__*/function () {
  function AtlBrowserStorage(storage, storageType) {
    _classCallCheck(this, AtlBrowserStorage);
    _defineProperty(this, "isAvailable", false);
    _defineProperty(this, "checkIfSetByPackage", /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
        var itemKey, _ref$isSetCallback, isSetCallback, _ref$isNotSetCallback, isNotSetCallback, record;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              itemKey = _ref.itemKey, _ref$isSetCallback = _ref.isSetCallback, isSetCallback = _ref$isSetCallback === void 0 ? function () {} : _ref$isSetCallback, _ref$isNotSetCallback = _ref.isNotSetCallback, isNotSetCallback = _ref$isNotSetCallback === void 0 ? function () {} : _ref$isNotSetCallback;
              _context.prev = 1;
              _context.next = 4;
              return BSCIndexedDB.getRecord(itemKey);
            case 4:
              record = _context.sent;
              if (!record) {
                _context.next = 10;
                break;
              }
              isSetCallback();
              return _context.abrupt("return", true);
            case 10:
              isNotSetCallback();
              return _context.abrupt("return", false);
            case 12:
              _context.next = 19;
              break;
            case 14:
              _context.prev = 14;
              _context.t0 = _context["catch"](1);
              // If it fails to pull from IDB, we can't validate that it's been set by our package or not, so log the error
              // and assume it hasn't been set by our package. This will only impact our analytics correctness.
              Logger.errorWithOperationalEvent({
                action: 'usedCheckIfSetByPackageError',
                message: "IDB lookup failed to check if item was set by package. ".concat(_context.t0.message || ''),
                attributes: {
                  itemKey: itemKey
                }
              });
              isNotSetCallback();
              return _context.abrupt("return", false);
            case 19:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[1, 14]]);
      }));
      return function (_x) {
        return _ref2.apply(this, arguments);
      };
    }());
    this.storage = storage;
    this.storageType = storageType;
    this.isAvailable = this.isStorageAvailable();
  }
  return _createClass(AtlBrowserStorage, [{
    key: "isWebStorageEnabled",
    get: function get() {
      // Note: outside of user configuration, webStoragedEnabled is also feature gated by
      // 1. platform_moonjelly_bsc_master_killswitch
      // 2. platform_moonjelly_bsc_web_storage_killswitch
      if (getBscGlobalState().webStorageEnabled) {
        return true;
      }
      Logger.warn('WebStorage config is disabled, cannot proceed.');
      return false;
    }

    // Core API
  }, {
    key: "getItem",
    value: function getItem(key) {
      if (!this.isWebStorageEnabled) {
        return this.storage.getItem(key);
      }
      if (!this.isAvailable) {
        // If our storage hasn't been enabled, short-circuit because we can't use the native utility
        Logger.warn("".concat(this.storageType, " is unavailable. Failed to get item ").concat(key));
        return null;
      }
      var retrievedItem = getStorageItem(key, this.storageType, this.storage);
      return retrievedItem;
    }
  }, {
    key: "setItem",
    value: function () {
      var _setItem = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(key, value) {
        var globalStateProduct, itemSetStatus;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              if (this.isWebStorageEnabled) {
                _context2.next = 3;
                break;
              }
              this.storage.setItem(key, value);
              return _context2.abrupt("return");
            case 3:
              if (this.isAvailable) {
                _context2.next = 6;
                break;
              }
              // If our storage hasn't been enabled, short-circuit because we can't use the native utility
              Logger.warn("".concat(this.storageType, " is unavailable. Failed to set item ").concat(key));
              return _context2.abrupt("return");
            case 6:
              globalStateProduct = getBscGlobalState().product;
              _context2.next = 9;
              return setStorageItem(key, value, this.storageType, globalStateProduct, this.storage);
            case 9:
              itemSetStatus = _context2.sent;
              Logger.debug("".concat(itemSetStatus === Status.SUCCESS ? 'Successfully' : 'Failed to', " set item ").concat(key, " with value ").concat(value));
            case 11:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function setItem(_x2, _x3) {
        return _setItem.apply(this, arguments);
      }
      return setItem;
    }()
  }, {
    key: "setStrictlyNecessaryItem",
    value: function setStrictlyNecessaryItem(key, value) {
      if (!this.isWebStorageEnabled) {
        this.storage.setItem(key, value);
        return;
      }
      if (!this.isAvailable) {
        // If our storage hasn't been enabled, short-circuit because we can't use the native utility
        Logger.warn("".concat(this.storageType, " is unavailable. Failed to set strictly necessary item ").concat(key));
        return;
      }
      var itemSetStatus = setStrictlyNecessaryStorageItem(key, value, this.storageType, this.storage);
      Logger.debug("".concat(itemSetStatus === Status.SUCCESS ? 'Successfully' : 'Failed to', " set strictly necessary item ").concat(key, " with value ").concat(value));
    }

    // Native Utilities
  }, {
    key: "removeItem",
    value: function removeItem(key) {
      if (!this.isAvailable) {
        // If our storage hasn't been enabled, short-circuit because we can't use the native utility
        Logger.warn("".concat(this.storageType, " is unavailable. Failed to remove item ").concat(key));
        return;
      }
      this.storage.removeItem(key);
    }
  }, {
    key: "key",
    value: function key(index) {
      if (!this.isAvailable) {
        return null;
      }
      return this.storage.key(index);
    }
  }, {
    key: "length",
    value: function length() {
      if (!this.isAvailable) {
        return 0;
      }
      return this.storage.length;
    }
  }, {
    key: "clear",
    value: function clear() {
      if (!this.isAvailable) {
        return;
      }
      this.storage.clear();
    }

    // Utilities
  }, {
    key: "isStorageAvailable",
    value: function isStorageAvailable() {
      if (isSSR()) {
        return false;
      }
      try {
        var x = '__storage_test__';
        this.storage.setItem(x, x);
        this.storage.removeItem(x);
        return true;
      } catch (e) {
        return e instanceof DOMException && (
        // everything except Firefox
        e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        this.storage && this.storage.length !== 0;
      }
    }
  }]);
}();
var AtlBrowserStorageLocal = new AtlBrowserStorage((_window = window) === null || _window === void 0 ? void 0 : _window.localStorage, WebStorageType.LocalStorage);
var AtlBrowserStorageSession = new AtlBrowserStorage((_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.sessionStorage, WebStorageType.SessionStorage);
export var findStorageTarget = function findStorageTarget(type) {
  return _defineProperty(_defineProperty({}, WebStorageType.LocalStorage, AtlBrowserStorageLocal), WebStorageType.SessionStorage, AtlBrowserStorageSession)[type];
};
export { AtlBrowserStorage, AtlBrowserStorageSession, AtlBrowserStorageLocal, WebStorageType };